import React, { useState, useEffect, useRef, useMemo } from "react";
import { Row, Col, Tabs, Spin } from "antd";
import { ArrowLeftOutlined, LoadingOutlined } from "@ant-design/icons";
import { useTheme } from "../../../contexts/ThemeContext";
import { Button, Toast } from "../../ui";
// import { ProjectHelpCard } from '../../projects';
import TypeSelector from "./TypeSelector";
import CategoryList from "./CategoryList";
import FinancialIndicatorsConfig from "./FinancialIndicatorsConfig";
import DragDropConfigurator from "./DragDropConfigurator"; // Importar novo componente
import SynchronizationService from "../../../services/synchronizationService";
import { useParameterConfig } from "../../../hooks/useParameterConfig";
import { useCategoryActions } from "../../../hooks/useCategoryActions";
import DRETreeManager from "./DREConfig/DreTreeManager";
import { clearStandardReportsCacheForSync } from "../../../pages/Reports/standard/services/standardReportsCache";
import {
  filterOmieFinancialCategories,
  filterOmieInvalidKpiOrGroupConfigRows,
} from "../../../utils/omieFinancialCategoryFilter";

// Utilitário: normaliza e deduplica por (tipo + categoria), preferindo itens com Id
const normalizeAndDedup = (arr) => {
  if (!Array.isArray(arr)) return [];
  const map = new Map();
  arr.forEach((item) => {
    const typeVal = Number(
      item.type !== undefined
        ? item.type
        : item.Type !== undefined
          ? item.Type
          : item.financialGroup !== undefined
            ? item.financialGroup
            : item.FinancialGroup !== undefined
              ? item.FinancialGroup
              : 0,
    );
    const cat = item.codCategoria || item.CodCategoria;
    const key = `${typeVal}|${cat}`;
    if (!map.has(key)) {
      map.set(key, item);
    } else {
      const existing = map.get(key);
      if (!existing.Id && !existing.id && (item.Id || item.id)) {
        map.set(key, item);
      }
    }
  });
  return Array.from(map.values());
};

const SynchronizationParametersConfigRefactored = ({
  parameterType,
  onBack,
  onSave,
  syncId,
  existingConfigs = [],
  onParametersChange,
  categoryList = [],
  syncName,
  embedded,
  menu,
  submenu,
  onDreTabChange,
  erp,
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [syncMeta, setSyncMeta] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!syncId) return;
      try {
        const res = await SynchronizationService.getSynchronizationById(syncId);
        const data = res?.response || res;
        if (!alive) return;
        setSyncMeta({
          erp: data?.erp ?? data?.Erp ?? erp ?? null,
          externalId:
            data?.externalId ??
            data?.ExternalId ??
            data?.integrationId ??
            data?.IntegrationId ??
            null,
        });
      } catch {
        if (!alive) return;
        setSyncMeta((prev) => prev || { erp: erp ?? null, externalId: null });
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [syncId, erp]);

  const invalidateStandardReportsCacheForThisSync = useMemo(() => {
    const erpValue = syncMeta?.erp ?? erp ?? null;
    const externalIdValue = syncMeta?.externalId ?? null;
    return () => {
      clearStandardReportsCacheForSync({
        syncId,
        erp: erpValue,
        externalId: externalIdValue,
        types: ["dre", "dfc", "visual", "dreConfig", "dfcConfig"],
      });
    };
  }, [syncId, syncMeta?.erp, syncMeta?.externalId, erp]);
  useEffect(() => {
    if (parameterType === "dre") {
      const scrollTopSafe = () => {
        try {
          const containers = document.querySelectorAll(
            ".ant-drawer-body, .ant-modal-body",
          );
          containers.forEach((el) => {
            try {
              el.scrollTo({ top: 0, behavior: "auto" });
            } catch {}
          });
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        } catch {}
      };
      const t = setTimeout(scrollTopSafe, 0);
      return () => clearTimeout(t);
    }
  }, [parameterType, syncId]);

  // --- DRE: Estado e handlers exclusivos ---
  const dreConfig = existingConfigs.dreConfig || [];

  const [financialIndicators, setFinancialIndicators] = useState(false);
  // DRE live state to feed FinancialIndicatorsConfig
  const [dreNodes, setDreNodes] = useState(dreConfig?.syncDre || []);
  const [dreCats, setDreCats] = useState(dreConfig?.syncDreCat || []);
  // KPI payload built by FinancialIndicatorsConfig
  const [kpiPayload, setKpiPayload] = useState([]);

  // Memoize effectiveDreNodes to prevent infinite loops in FinancialIndicatorsConfig
  // caused by passing a new array reference [] on every render when dreNodes/dreConfig is empty
  const effectiveDreNodes = useMemo(() => {
    if (dreNodes && dreNodes.length > 0) return dreNodes;
    return dreConfig?.syncDre || [];
  }, [dreNodes, dreConfig]);

  // Estado para busca no contexto DRE
  const [dreSearchTerm, setDreSearchTerm] = useState("");

  // --- Hooks e estados para os outros parâmetros ---
  const [memoryByType, setMemoryByType] = useState({});
  const [expandedCategoriesByType, setExpandedCategoriesByType] = useState({});
  const prevTypeRef = useRef(null);

  // Refs for embedded configs (KPI, Financial Groups)
  const kpiSaveRef = useRef(null);
  const financialGroupSaveRef = useRef(null);

  // Filtered list for embedded configs (no Revenue); Omie: exclui categorias calculadas
  // Usado para Indicadores (KPI), que deve excluir receitas.
  const categoryListNoRevenue = useMemo(() => {
    let list = categoryList.filter((cat) => cat.natureza !== "R");
    if (Number(erp) === 0) {
      list = filterOmieFinancialCategories(list);
    }
    return list;
  }, [categoryList, erp]);

  // Lista para Grupos Financeiros: inclui receitas (categorias-folha de receita
  // devem ser selecionáveis). Mantém o filtro Omie quando ERP = 0.
  const categoryListForFinancialGroups = useMemo(() => {
    let list = categoryList;
    if (Number(erp) === 0) {
      list = filterOmieFinancialCategories(list);
    }
    return list;
  }, [categoryList, erp]);

  // Só inicializa hooks se NÃO for DRE
  const isDRE = parameterType === "dre";
  const dreTitle = syncName
    ? `Configurando o DRE para: ${syncName}`
    : "Configuração do DRE";
  const paramConfig = isDRE
    ? {
        title: dreTitle,
        subtitle: "Organize e edite as categorias do DRE conforme necessário.",
      }
    : undefined;

  // Filtrar categorias de receita apenas para Indicadores; Grupos Financeiros
  // mantém receitas para permitir folhas de receita selecionáveis.
  // Omie: exclui categorias calculadas tanto em Indicadores quanto em Grupos Financeiros.
  const filteredCategoryList = useMemo(() => {
    let list = categoryList;
    if (parameterType === "indicadores") {
      list = categoryList.filter((cat) => cat.natureza !== "R");
    }
    if (
      Number(erp) === 0 &&
      (parameterType === "indicadores" ||
        parameterType === "gruposFinanceiros")
    ) {
      list = filterOmieFinancialCategories(list);
    }
    return list;
  }, [categoryList, parameterType, erp]);

  // Hooks para outros parâmetros
  const parameterHooks = !isDRE ? { useParameterConfig } : null;
  const categoryActionsHooks = !isDRE ? { useCategoryActions } : null;

  // Só usa os hooks se não for DRE
  const {
    configurations,
    selectedCategories,
    expandedCategories,
    selectedType,
    searchTerm,
    paramConfig: paramConfigHook,
    setSelectedCategories,
    setExpandedCategories,
    setSelectedType,
    setSearchTerm,
    setConfigurations,
    getTypeFromCategory,
    getCategoriesForType,
    buildParametersObject,
  } = !isDRE && parameterHooks
    ? parameterHooks.useParameterConfig(
        parameterType,
        syncId,
        null,
        onParametersChange,
        filteredCategoryList,
        syncName,
      )
    : {};

  // Substituir paramConfig se não for DRE
  const finalParamConfig = isDRE ? paramConfig : paramConfigHook;

  // Carregar on-demand os parâmetros não-DRE ao acessar a tela
  useEffect(() => {
    const loadParams = async () => {
      if (isDRE || !syncId) return;
      try {
        setLoading(true);
        let res;
        let key;
        if (parameterType === "dfc") {
          res = await SynchronizationService.getDFCConfig(syncId);
          key = "dfcConfig";
        } else if (parameterType === "gruposFinanceiros") {
          res = await SynchronizationService.getFinancialGroupConfig(syncId);
          key = "financialGroupConfig";
        } else if (parameterType === "indicadores") {
          res = await SynchronizationService.getKPIConfig(syncId);
          key = "kpiConfig";
        }
        const list = (res && (res.response || res)) || [];
        let deduped = normalizeAndDedup(list);
        if (
          Number(erp) === 0 &&
          (key === "kpiConfig" || key === "financialGroupConfig")
        ) {
          deduped = filterOmieInvalidKpiOrGroupConfigRows(
            deduped,
            categoryList,
          );
        }
        if (Array.isArray(deduped) && setConfigurations) {
          setConfigurations((prev) => ({
            ...(prev || {
              dfcConfig: [],
              financialGroupConfig: [],
              kpiConfig: [],
              dreConfig: [],
            }),
            [key]: deduped,
          }));
        }
      } catch (error) {
        console.error("Erro ao carregar parâmetros:", error);
        Toast.error("Erro ao carregar parâmetros");
      } finally {
        setLoading(false);
      }
    };
    loadParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterType, syncId, erp]);

  // Reaplica exclusão Omie quando a lista de categorias fica disponível (lookup por descrição)
  useEffect(() => {
    if (isDRE || !syncId || Number(erp) !== 0) return;
    if (
      parameterType !== "indicadores" &&
      parameterType !== "gruposFinanceiros"
    )
      return;
    if (!setConfigurations || !Array.isArray(categoryList)) return;
    const key =
      parameterType === "gruposFinanceiros"
        ? "financialGroupConfig"
        : "kpiConfig";
    setConfigurations((prev) => {
      const rows = prev?.[key] || [];
      const pruned = filterOmieInvalidKpiOrGroupConfigRows(rows, categoryList);
      if (pruned.length === rows.length) return prev;
      return { ...prev, [key]: pruned };
    });
  }, [categoryList, erp, parameterType, syncId, isDRE, setConfigurations]);

  // Mapa de categorias desabilitadas quando já pertencem a outro tipo (somente não-DRE)
  const disabledCategoriesMap = useMemo(() => {
    if (
      isDRE ||
      !configurations ||
      selectedType === null ||
      selectedType === undefined
    )
      return {};
    const key = {
      dfc: "dfcConfig",
      gruposFinanceiros: "financialGroupConfig",
      indicadores: "kpiConfig",
    }[parameterType];
    const list = configurations && key ? configurations[key] || [] : [];
    const map = {};
    list.forEach((item) => {
      const cfgType = Number(
        item.type !== undefined
          ? item.type
          : item.Type !== undefined
            ? item.Type
            : item.financialGroup !== undefined
              ? item.financialGroup
              : item.FinancialGroup !== undefined
                ? item.FinancialGroup
                : -1,
      );
      const cat = item.codCategoria || item.CodCategoria;
      if (cat && cfgType !== Number(selectedType)) {
        map[cat] = true; // bloquear edição se já estiver em outro tipo
      }
    });
    return map;
  }, [isDRE, configurations, selectedType, parameterType]);

  // Handle Save logic for DFC (when using DragDrop)
  // We need to override the default save logic for DFC to use the configurations state directly
  // similar to how we did for embedded KPI/FinancialGroups

  // Salvar individualmente por parâmetro (não-DRE)
  const handleSaveCurrentParameter = async (saveContext = {}) => {
    // Se saveContext vier do DRETreeManager, usamos o contexto de abas
    const {
      nodes: dreNodesFromChild,
      cats: dreCatsFromChild,
      activeTab,
      activeIndicatorSubMenu,
      dreConfiguratorReadOnly,
      tipoDrePag,
      tipoDrePagDirty,
    } = saveContext;

    if (isDRE) {
      const isStructureTab = activeTab === "Structure" || activeTab === undefined;
      const currentNodes = dreNodesFromChild || dreNodes;
      const currentCats = dreCatsFromChild || dreCats;

      if (dreConfiguratorReadOnly && isStructureTab) {
        const structuralDirtyNodes = Array.isArray(currentNodes)
          ? currentNodes.filter((n) => n.isDirty || n.isNew)
          : [];
        const catsAlteradasReadOnly = Array.isArray(currentCats)
          ? currentCats.filter((c) => c.isDirty || c.isNew)
          : [];

        if (structuralDirtyNodes.length > 0 || catsAlteradasReadOnly.length > 0) {
          Toast.warning(
            "O DRE está vinculado ao Conta Azul. A estrutura não pode ser alterada neste modo.",
          );
          return;
        }

        Toast.warning("Nenhuma alteração estrutural para salvar.");
        return;
      }

      // Salvar DRE + KPIs para a tela de DRE
      try {
        setLoading(true);
        const promises = [];

        // Lógica de salvamento baseada na aba ativa
        const isStructureTab = activeTab === "Structure";
        const isIndicatorsTab = activeTab === "Indicators";

        // 1) Salvar ESTRUTURA DRE
        if (isStructureTab || activeTab === undefined) {
          let nodosAlteradosRaw = Array.isArray(currentNodes)
            ? currentNodes.filter((n) => n.isDirty || n.isNew)
            : [];
          let catsAlteradasRaw = Array.isArray(currentCats)
            ? currentCats.filter((c) => c.isDirty || c.isNew)
            : [];

          // ... (sorting logic) ...
          const sortNodesDeterministic = (arr) => {
            const totals = arr
              .filter((n) => n.isTotalizador)
              .sort(
                (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0),
              );
            const contas = arr
              .filter((n) => n.isConta)
              .sort((a, b) => {
                const sa = String(a.codDRESup || "");
                const sb = String(b.codDRESup || "");
                if (sa !== sb)
                  return sa.localeCompare(sb, "pt-BR", { numeric: true });
                return (Number(a.position) || 0) - (Number(b.position) || 0);
              });
            const subs = arr
              .filter((n) => n.isSubConta)
              .sort((a, b) => {
                const sa = String(a.codDRESup || "");
                const sb = String(b.codDRESup || "");
                if (sa !== sb)
                  return sa.localeCompare(sb, "pt-BR", { numeric: true });
                return (Number(a.position) || 0) - (Number(b.position) || 0);
              });
            return [...totals, ...contas, ...subs];
          };
          const dedupeCats = (arr) => {
            const map = new Map();
            for (const c of arr) {
              const key = `${String(c.syncDREId || "")}|${String(c.codCategoria || "")}`;
              map.set(key, c);
            }
            return Array.from(map.values()).sort((a, b) => {
              const sa = String(a.syncDREId || "");
              const sb = String(b.syncDREId || "");
              if (sa !== sb)
                return sa.localeCompare(sb, "pt-BR", { numeric: true });
              return String(a.codCategoria || "").localeCompare(
                String(b.codCategoria || ""),
                "pt-BR",
                { numeric: true },
              );
            });
          };

          const nodosAlterados = sortNodesDeterministic(nodosAlteradosRaw);
          const catsAlteradas = dedupeCats(catsAlteradasRaw);
          // A regra DRE por pagamento participa do controle de alterações: dispara o saveDreConfig
          // mesmo quando só ela mudou, e é enviada no mesmo contrato (sem endpoint separado).
          if (
            nodosAlterados.length > 0 ||
            catsAlteradas.length > 0 ||
            tipoDrePagDirty
          ) {
            promises.push(
              SynchronizationService.saveSynchronizationDreConfig(
                nodosAlterados,
                catsAlteradas,
                syncId,
                tipoDrePag ?? null,
              ),
            );
          }
        }

        if (isIndicatorsTab) {
          // 2) Salvar Indicadores (KPI DRE - Financeiros)
          if (activeIndicatorSubMenu === "financial") {
            if (Array.isArray(kpiPayload) && kpiPayload.length > 0) {
              promises.push(
                SynchronizationService.upsertSyncKPIDRE(kpiPayload),
              );
            }
          }

          // Dedupe helper for embedded configs
          const dedupePayload = (arr) => {
            const map = new Map();
            arr.forEach((item) => {
              const typeVal = Number(
                item.type !== undefined
                  ? item.type
                  : item.Type !== undefined
                    ? item.Type
                    : item.financialGroup !== undefined
                      ? item.financialGroup
                      : item.FinancialGroup !== undefined
                        ? item.FinancialGroup
                        : 0,
              );
              const cat = item.codCategoria || item.CodCategoria;
              const composite = `${typeVal}|${cat}`;
              if (!map.has(composite)) {
                map.set(composite, item);
              } else {
                const existing = map.get(composite);
                if (!existing.Id && !existing.id && (item.Id || item.id)) {
                  map.set(composite, item);
                }
              }
            });
            return Array.from(map.values());
          };

          // 3) Salvar KPI Config (Categorias KPI)
          if (activeIndicatorSubMenu === "kpi" && kpiSaveRef.current) {
            const obj = kpiSaveRef.current();
            const kpiPayloadFromRef = obj.kpiConfig || [];
            const deduped = dedupePayload(kpiPayloadFromRef);
            const transformed = deduped.map((item) => ({
              Id: item.id || item.Id || null,
              Type: item.type ?? item.Type ?? 0,
              CodCategoria: item.codCategoria || item.CodCategoria,
              SyncId: syncId,
            }));
            if (transformed.length > 0) {
              promises.push(SynchronizationService.saveKPIConfig(transformed));
            }
          }

          // 4) Salvar Financial Groups
          if (
            activeIndicatorSubMenu === "financial_groups" &&
            financialGroupSaveRef.current
          ) {
            const obj = financialGroupSaveRef.current();
            const fgPayloadFromRef = obj.financialGroupConfig || [];
            const deduped = dedupePayload(fgPayloadFromRef);
            const transformed = deduped.map((item) => ({
              Id: item.id || item.Id || null,
              FinancialGroup: item.financialGroup ?? item.FinancialGroup ?? 0,
              CodCategoria: item.codCategoria || item.CodCategoria,
              SyncId: syncId,
            }));
            if (transformed.length > 0) {
              promises.push(
                SynchronizationService.saveFinancialGroupConfig(transformed),
              );
            }
          }
        }

        if (promises.length === 0) {
          Toast.warning("Nenhuma alteração para salvar na aba atual");
          return;
        }

        await Promise.all(promises);
        invalidateStandardReportsCacheForThisSync();
        Toast.success("Alterações salvas com sucesso!");
      } catch (error) {
        console.error("Erro ao salvar DRE/Indicadores:", error);
        Toast.error("Erro ao salvar alterações");
      } finally {
        setLoading(false);
      }
      return;
    }

    // --- Non-DRE Save Logic (DFC, Financial Groups direct, KPI direct) ---
    try {
      setLoading(true);

      let payload = [];
      let saver = null;

      if (parameterType === "dfc") {
        // Use configurations state directly for DFC when using DragDrop
        payload = configurations.dfcConfig || [];
        saver = SynchronizationService.saveDFCConfig;
      } else if (parameterType === "gruposFinanceiros") {
        payload = configurations.financialGroupConfig || [];
        saver = SynchronizationService.saveFinancialGroupConfig;
      } else if (parameterType === "indicadores") {
        payload = configurations.kpiConfig || [];
        saver = SynchronizationService.saveKPIConfig;
      } else {
        // Fallback to buildParametersObject for others (if any)
        const resultObj = buildParametersObject ? buildParametersObject() : {};
        // ... (legacy logic if needed, but we are migrating DFC to DragDrop)
      }

      // Deduplicar payload por tipo + categoria
      const dedupePayload = (arr) => {
        const map = new Map();
        arr.forEach((item) => {
          const typeVal = Number(
            item.type !== undefined
              ? item.type
              : item.Type !== undefined
                ? item.Type
                : item.financialGroup !== undefined
                  ? item.financialGroup
                  : item.FinancialGroup !== undefined
                    ? item.FinancialGroup
                    : 0,
          );
          const cat = item.codCategoria || item.CodCategoria;
          const composite = `${typeVal}|${cat}`;
          if (!map.has(composite)) {
            map.set(composite, item);
          } else {
            const existing = map.get(composite);
            // Preferir o que possui Id
            if (!existing.Id && !existing.id && (item.Id || item.id)) {
              map.set(composite, item);
            }
          }
        });
        return Array.from(map.values());
      };

      payload = Array.isArray(payload) ? dedupePayload(payload) : [];

      // Transform payload to PascalCase for backend if needed
      const transformedPayload = payload.map((item) => ({
        Id: item.id || item.Id || null,
        // Handle dynamic field mapping based on parameterType
        ...(parameterType === "gruposFinanceiros"
          ? { FinancialGroup: item.financialGroup ?? item.FinancialGroup ?? 0 }
          : { Type: item.type ?? item.Type ?? 0 }),
        CodCategoria: item.codCategoria || item.CodCategoria,
        SyncId: syncId,
      }));

      if (
        !Array.isArray(transformedPayload) ||
        transformedPayload.length === 0
      ) {
        Toast.warning("Nenhuma configuração selecionada para salvar");
        return;
      }

      const res = await saver(transformedPayload);
      const saved = (res && (res.response || res)) || null;

      if (saved) {
        Toast.success("Parâmetros salvos com sucesso!");
        if (parameterType === "indicadores" || parameterType === "gruposFinanceiros") {
          invalidateStandardReportsCacheForThisSync();
        }
        // Recarregar lista do backend
        if (parameterType === "dfc") {
          const reload = await SynchronizationService.getDFCConfig(syncId);
          const list = (reload && (reload.response || reload)) || [];
          setConfigurations((prev) => ({
            ...(prev || {}),
            dfcConfig: normalizeAndDedup(list),
          }));
        } else if (parameterType === "gruposFinanceiros") {
          // ... (similar reload logic)
          const reload =
            await SynchronizationService.getFinancialGroupConfig(syncId);
          const list = (reload && (reload.response || reload)) || [];
          setConfigurations((prev) => ({
            ...(prev || {}),
            financialGroupConfig: normalizeAndDedup(list),
          }));
        } else if (parameterType === "indicadores") {
          const reload = await SynchronizationService.getKPIConfig(syncId);
          const list = (reload && (reload.response || reload)) || [];
          setConfigurations((prev) => ({
            ...(prev || {}),
            kpiConfig: normalizeAndDedup(list),
          }));
        }
      } else {
        Toast.success("Parâmetros enviados para salvar");
      }
    } catch (error) {
      console.error("Erro ao salvar parâmetros:", error);
      Toast.error("Erro ao salvar parâmetros");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeFinancialIndicators = () => {
    setFinancialIndicators(!financialIndicators);
  };

  // Handlers e lógica para outros parâmetros
  const handleTypeChange = !isDRE
    ? (newType) => {
        if (newType === null || newType === undefined) return;
        if (prevTypeRef.current !== null && prevTypeRef.current !== undefined) {
          setMemoryByType((prev) => ({
            ...prev,
            [prevTypeRef.current]: {
              selectedCategories,
              expandedCategories,
            },
          }));
          setExpandedCategoriesByType((prev) => ({
            ...prev,
            [prevTypeRef.current]: expandedCategories,
          }));
        }
        const mem = memoryByType[newType] || {};
        const configKey = {
          dfc: "dfcConfig",
          gruposFinanceiros: "financialGroupConfig",
          indicadores: "kpiConfig",
          dre: "dreConfig",
          orcamento: "orcamentoConfig",
        }[parameterType];
        const currentConfigs = configurations[configKey] || [];
        const configSelected = {};
        currentConfigs.forEach((config) => {
          const configType = Number(
            config.type !== undefined
              ? config.type
              : config.Type !== undefined
                ? config.Type
                : config.financialGroup !== undefined
                  ? config.financialGroup
                  : config.FinancialGroup !== undefined
                    ? config.FinancialGroup
                    : -1,
          );
          if (configType === Number(newType)) {
            configSelected[config.codCategoria || config.CodCategoria] = true;
          }
        });
        const memorySelected = mem.selectedCategories || {};
        const combinedSelected = { ...configSelected, ...memorySelected };
        setSelectedCategories(combinedSelected);
        setExpandedCategories(
          expandedCategoriesByType[newType] || mem.expandedCategories || {},
        );
        setSelectedType(newType);
        prevTypeRef.current = newType;
      }
    : undefined;

  const handleSearchChangeWithMemory = !isDRE
    ? (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (!value) {
          setExpandedCategories(expandedCategoriesByType[selectedType] || {});
        }
      }
    : undefined;

  // Category actions para outros parâmetros
  const categoryActions =
    !isDRE && categoryActionsHooks
      ? categoryActionsHooks.useCategoryActions(
          parameterType,
          syncId,
          selectedCategories,
          setSelectedCategories,
          configurations,
          setConfigurations,
          expandedCategories,
          setExpandedCategories,
          setSearchTerm,
          getTypeFromCategory,
          filteredCategoryList,
          selectedType,
          onParametersChange,
          disabledCategoriesMap,
        )
      : {};

  // Toggle expanded para outros parâmetros
  const toggleExpanded = !isDRE
    ? (categoryId) => {
        setExpandedCategories((prev) => {
          const updated = { ...prev, [categoryId]: !prev[categoryId] };
          setExpandedCategoriesByType((prevGroups) => ({
            ...prevGroups,
            [selectedType]: updated,
          }));
          return updated;
        });
      }
    : undefined;

  // Efeito para manter prevTypeRef atualizado (outros parâmetros)
  useEffect(() => {
    if (!isDRE) {
      prevTypeRef.current = selectedType;
      if (selectedType !== null && selectedType !== undefined) {
        setMemoryByType((prev) => {
          const cleaned = { ...prev };
          Object.keys(cleaned).forEach((typeKey) => {
            if (Number(typeKey) !== Number(selectedType)) {
              delete cleaned[typeKey];
            }
          });
          return cleaned;
        });
      }
    }
  }, [selectedType, isDRE]);

  // disabledCategoriesMap agora é calculado via useMemo acima

  // --- Salvar ---
  // O salvamento individual é tratado por handleSaveCurrentParameter

  // --- Renderização ---
  if (!finalParamConfig) {
    return <div>Tipo de parâmetro não suportado: {parameterType}</div>;
  }

  // Se está mostrando indicadores financeiros
  if (financialIndicators) {
    return (
      <FinancialIndicatorsConfig
        onBack={() => setFinancialIndicators(false)}
        onSave={(config) => {
          setFinancialIndicators(false);
          Toast.success("Indicadores financeiros configurados com sucesso!");
        }}
        syncId={syncId}
        categoryList={filteredCategoryList}
        existingConfig={existingConfigs.kpiConfig}
        syncName={syncName}
      />
    );
  }

  // --- Renderização para DFC (com DragDrop) ---
  if (parameterType === "dfc") {
    const handleDfcConfigChange = (newConfig) => {
      setConfigurations((prev) => ({
        ...prev,
        dfcConfig: newConfig,
      }));
    };

    return (
      <div style={{ minHeight: "100vh" }}>
        <div style={{ width: "100%", margin: "0 auto" }}>
          {/* Navegação */}
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            style={{ padding: 8, color: theme.primary, marginBottom: 16 }}
          >
            Voltar para Configurações
          </Button>
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 24,
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 24,
                  fontWeight: 600,
                  color: theme.primary,
                }}
              >
                {finalParamConfig.title}
              </h1>
              <p
                style={{
                  margin: "4px 0 0",
                  color: theme.textSecondary,
                  fontSize: 14,
                  whiteSpace: "pre-line", // Permite quebras de linha se houver na descrição
                }}
              >
                {finalParamConfig.subtitle}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                type="primary"
                loading={loading}
                onClick={() => handleSaveCurrentParameter()}
              >
                Salvar configurações
              </Button>
            </div>
          </div>

          {/* Conteúdo DFC DragDrop */}
          <div style={{ padding: 0 }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: "center" }}>
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
                />
                <p>Carregando configurações...</p>
              </div>
            ) : (
              <DragDropConfigurator
                types={finalParamConfig.types}
                categories={categoryList}
                currentConfig={configurations.dfcConfig || []}
                onConfigChange={handleDfcConfigChange}
                parameterType="dfc"
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ width: "100%", margin: "0 auto" }}>
        {/* Navegação */}
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{ padding: 8, color: theme.primary, marginBottom: 16 }}
        >
          Voltar para Configurações
        </Button>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 600,
                color: theme.primary,
              }}
            >
              {finalParamConfig.title}
            </h1>
            <p
              style={{
                margin: "4px 0 0",
                color: theme.textSecondary,
                fontSize: 14,
              }}
            >
              {finalParamConfig.subtitle}
            </p>
          </div>
          {isDRE ? null : (
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={onBack}>Voltar</Button>
              <Button
                type="primary"
                loading={loading}
                onClick={() => handleSaveCurrentParameter()}
              >
                Salvar configurações
              </Button>
            </div>
          )}
        </div>
        <div>
          {/* Configuração por tabs quando DRE */}
          {isDRE ? (
            <div style={{ padding: 0 }}>
              <DRETreeManager
                initialCategories={dreConfig.syncDreCat}
                initialNodes={dreConfig.syncDre}
                syncId={syncId}
                onSave={handleSaveCurrentParameter}
                isSaving={loading}
                searchTerm={dreSearchTerm}
                onSearchChange={setDreSearchTerm}
                onDreChange={({ nodes, cats }) => {
                  setDreNodes(nodes);
                  setDreCats(cats);
                }}
                isEmbedded={embedded}
                menu={menu}
                submenu={submenu}
                onTabChange={onDreTabChange}
                indicatorsContent={
                  <FinancialIndicatorsConfig
                    syncId={syncId}
                    categoryList={filteredCategoryList}
                    existingConfig={existingConfigs.kpiConfig}
                    dreNodes={effectiveDreNodes}
                    onKpiChange={setKpiPayload}
                  />
                }
                kpiContent={
                  <EmbeddedConfigManager
                    parameterType="indicadores"
                    syncId={syncId}
                    categoryList={categoryListNoRevenue}
                    syncName={syncName}
                    saveRef={kpiSaveRef}
                    existingConfigs={existingConfigs}
                    externalSearchTerm={dreSearchTerm}
                    erp={erp}
                  />
                }
                financialGroupsContent={
                  <EmbeddedConfigManager
                    parameterType="gruposFinanceiros"
                    syncId={syncId}
                    categoryList={categoryListForFinancialGroups}
                    syncName={syncName}
                    saveRef={financialGroupSaveRef}
                    existingConfigs={existingConfigs}
                    externalSearchTerm={dreSearchTerm}
                    erp={erp}
                  />
                }
              />
            </div>
          ) : (
            <>
              <div style={{ padding: 0 }}>
                <TypeSelector
                  selectedType={selectedType}
                  onTypeChange={handleTypeChange}
                  types={finalParamConfig.types}
                  onChangeFinancialIndicators={handleChangeFinancialIndicators}
                />
              </div>
              {selectedType !== null &&
                (!loading ? (
                  <CategoryList
                    selectedType={selectedType}
                    categories={categoryActions.getFilteredCategories(
                      getCategoriesForType(parameterType, selectedType),
                      searchTerm,
                    )}
                    selectedCategories={selectedCategories}
                    expandedCategories={expandedCategories}
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChangeWithMemory}
                    onCategoryToggle={categoryActions.handleCategoryToggle}
                    onChildToggle={categoryActions.handleChildToggle}
                    onToggleExpansion={toggleExpanded}
                    color={finalParamConfig.color}
                    disabledCategories={disabledCategoriesMap}
                    checkedCategories={{}}
                  />
                ) : (
                  <div style={{ padding: 16, color: theme.textSecondary }}>
                    Carregando parâmetros...
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente para gerenciar configurações embarcadas (KPI, Grupos Financeiros) dentro do DRE
const EmbeddedConfigManager = ({
  parameterType,
  syncId,
  categoryList,
  syncName,
  saveRef,
  existingConfigs,
  externalSearchTerm,
  erp,
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);

  const {
    configurations,
    selectedCategories,
    expandedCategories,
    selectedType,
    searchTerm: hookSearchTerm,
    paramConfig,
    setSelectedCategories,
    setExpandedCategories,
    setSelectedType,
    setSearchTerm: setHookSearchTerm,
    setConfigurations,
    getCategoriesForType,
    buildParametersObject,
  } = useParameterConfig(
    parameterType,
    syncId,
    existingConfigs,
    null,
    categoryList,
    syncName,
  );

  // Se externalSearchTerm for fornecido, usamos ele. Caso contrário, usamos o do hook.
  const searchTerm =
    externalSearchTerm !== undefined ? externalSearchTerm : hookSearchTerm;
  const setSearchTerm =
    externalSearchTerm !== undefined ? () => {} : setHookSearchTerm;

  useEffect(() => {
    // Busca individual ao montar
    setLoading(true);
    const fetchConfig = async () => {
      try {
        let data = [];
        if (parameterType === "indicadores") {
          const res = await SynchronizationService.getKPIConfig(syncId);
          data = res?.response || res || [];
        } else if (parameterType === "gruposFinanceiros") {
          const res =
            await SynchronizationService.getFinancialGroupConfig(syncId);
          data = res?.response || res || [];
        }

        let deduped = normalizeAndDedup(data);
        if (Number(erp) === 0) {
          deduped = filterOmieInvalidKpiOrGroupConfigRows(
            deduped,
            categoryList,
          );
        }

        // Atualizar diretamente as configurações do hook
        setConfigurations((prev) => ({
          ...prev,
          [parameterType === "gruposFinanceiros"
            ? "financialGroupConfig"
            : "kpiConfig"]: deduped,
        }));
      } catch (err) {
        console.error(`Erro ao buscar config ${parameterType}:`, err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterType, syncId, erp]);

  useEffect(() => {
    if (Number(erp) !== 0) return;
    const key =
      parameterType === "gruposFinanceiros"
        ? "financialGroupConfig"
        : "kpiConfig";
    setConfigurations((prev) => {
      const rows = prev?.[key] || [];
      const pruned = filterOmieInvalidKpiOrGroupConfigRows(rows, categoryList);
      if (pruned.length === rows.length) return prev;
      return { ...prev, [key]: pruned };
    });
  }, [categoryList, erp, parameterType, setConfigurations]);

  const { getFilteredCategories, handleCategoryToggle, handleChildToggle } =
    useCategoryActions(
      parameterType,
      syncId,
      selectedCategories,
      setSelectedCategories,
      configurations,
      setConfigurations,
      expandedCategories,
      setExpandedCategories,
      setSearchTerm,
      null,
      categoryList,
      selectedType,
      null,
      {},
    );

  useEffect(() => {
    if (saveRef) {
      // Para KPI e Grupos Financeiros, a fonte da verdade é o estado configurations atualizado pelo DragDropConfigurator
      // O buildParametersObject padrão tenta reconstruir baseado em selectedCategories/selectedType, o que não funciona bem com multi-tipos do DragDrop
      if (
        parameterType === "indicadores" ||
        parameterType === "gruposFinanceiros"
      ) {
        saveRef.current = () => {
          const configKey =
            parameterType === "gruposFinanceiros"
              ? "financialGroupConfig"
              : "kpiConfig";
          return { [configKey]: configurations[configKey] };
        };
      } else {
        saveRef.current = buildParametersObject;
      }
    }
  }, [saveRef, buildParametersObject, configurations, parameterType]);

  const handleTypeChange = (newType) => {
    setSelectedType(newType);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleExpanded = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleConfigChange = (newConfig) => {
    setConfigurations((prev) => {
      // Determina a chave correta baseada no parameterType
      const key =
        parameterType === "gruposFinanceiros"
          ? "financialGroupConfig"
          : "kpiConfig"; // Assume indicadores se não for grupos

      return {
        ...prev,
        [key]: newConfig,
      };
    });
  };

  if (loading || !paramConfig)
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
        <span style={{ color: theme.textSecondary }}>
          Carregando configurações...
        </span>
      </div>
    );

  // Usar DragDropConfigurator se for KPIs ou Grupos Financeiros
  if (
    parameterType === "indicadores" ||
    parameterType === "gruposFinanceiros"
  ) {
    const configKey =
      parameterType === "gruposFinanceiros"
        ? "financialGroupConfig"
        : "kpiConfig";
    const currentConfig = configurations[configKey] || [];

    return (
      <DragDropConfigurator
        types={paramConfig.types}
        categories={categoryList}
        currentConfig={currentConfig}
        onConfigChange={handleConfigChange}
        parameterType={parameterType}
        searchTerm={searchTerm}
        erp={erp}
      />
    );
  }

  return (
    <div>
      <div style={{ padding: 0 }}>
        <TypeSelector
          selectedType={selectedType}
          onTypeChange={handleTypeChange}
          types={paramConfig.types}
        />
      </div>
      {selectedType !== null && (
        <CategoryList
          selectedType={selectedType}
          categories={getFilteredCategories(
            getCategoriesForType(parameterType, selectedType),
            searchTerm,
          )}
          selectedCategories={selectedCategories}
          expandedCategories={expandedCategories}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onCategoryToggle={handleCategoryToggle}
          onChildToggle={handleChildToggle}
          onToggleExpansion={toggleExpanded}
          color={paramConfig.color}
          disabledCategories={{}}
          checkedCategories={{}}
        />
      )}
    </div>
  );
};

export default SynchronizationParametersConfigRefactored;
